import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BellIcon, XIcon } from "./Icons";
import "./NotificationBell.css";

const POLL_MS = 90_000;

export default function NotificationBell() {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const refreshUnread = useCallback(async () => {
    try {
      const res = await axios.get("/api/notifications/unread-count", { withCredentials: true });
      setUnreadCount(res.data?.count ?? 0);
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/notifications", { withCredentials: true, params: { limit: 30 } });
      setItems(res.data?.items ?? []);
      setUnreadCount(res.data?.unread_count ?? 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, POLL_MS);
    return () => clearInterval(t);
  }, [refreshUnread]);

  useEffect(() => {
    const onFocus = () => refreshUnread();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshUnread]);

  useEffect(() => {
    if (!open) return;
    loadList();
  }, [open, loadList]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.post(`/api/notifications/${id}/read`, {}, { withCredentials: true });
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
      refreshUnread();
    } catch {
      /* ignore */
    }
  };

  const dismissOne = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.post(`/api/notifications/${id}/dismiss`, {}, { withCredentials: true });
      setItems((prev) => prev.filter((n) => n.id !== id));
      refreshUnread();
    } catch {
      /* ignore */
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post("/api/notifications/read-all", {}, { withCredentials: true });
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnreadCount(0);
    } catch {
      /* ignore */
    }
  };

  const onItemClick = async (n) => {
    if (!n.read_at) await markRead(n.id);
    if (n.action_url) {
      setOpen(false);
      navigate(n.action_url);
    }
  };

  return (
    <div className="notifBell" ref={rootRef}>
      <button
        type="button"
        className="notifBell__toggle"
        aria-label="Notifications"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <BellIcon className="notifBell__icon" />
        {unreadCount > 0 ? <span className="notifBell__badge">{unreadCount > 99 ? "99+" : unreadCount}</span> : null}
      </button>

      {open ? (
        <div className="notifBell__panel" role="dialog" aria-label="Notifications">
          <div className="notifBell__panelHeader">
            <span>Notifications</span>
            <div className="notifBell__panelActions">
              {unreadCount > 0 ? (
                <button type="button" className="notifBell__linkBtn" onClick={markAllRead}>
                  Mark all read
                </button>
              ) : null}
              <button type="button" className="notifBell__close" aria-label="Close" onClick={() => setOpen(false)}>
                <XIcon className="notifBell__closeIcon" />
              </button>
            </div>
          </div>
          <div className="notifBell__list">
            {loading ? <div className="notifBell__hint">Loading…</div> : null}
            {!loading && items.length === 0 ? (
              <div className="notifBell__empty">You&apos;re all caught up.</div>
            ) : null}
            {items.map((n) => (
              <div
                key={n.id}
                className={`notifBell__item ${n.read_at ? "notifBell__item--read" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => onItemClick(n)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onItemClick(n);
                  }
                }}
              >
                <div className="notifBell__itemMain">
                  <div className="notifBell__itemTitle">{n.title}</div>
                  {n.body ? <div className="notifBell__itemBody">{n.body}</div> : null}
                  <div className="notifBell__itemMeta">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</div>
                </div>
                <button
                  type="button"
                  className="notifBell__dismiss"
                  aria-label="Dismiss"
                  onClick={(e) => dismissOne(n.id, e)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
